import { inject, Injectable } from "@angular/core";
import {
  ContentfulClientApi,
  createClient as createContentfulClient,
  EntryCollection,
  EntrySkeletonType
} from "contentful";
import { from, Observable, of } from "rxjs";
import { catchError } from "rxjs/operators";
import { FeatureFlag } from "../../enums/feature-flag";
import { AppConfig } from "../../helpers/app.config";
import { ContentfulContentType } from "../../interfaces/contentful";
import { AuthService } from "../auth.service";
import { FeatureFlagService } from "../feature-flag.service";

const PUBLIC_CONTENT_TYPES = [
  ContentfulContentType.NBRegulatedModulesNews,
  ContentfulContentType.NBServicesModulesNews,
  ContentfulContentType.CPRegulatedModulesNews,
  ContentfulContentType.CPServicesModulesNews
];

@Injectable({ providedIn: "root" })
export class ContentfulService {
  private embargoedClient: ContentfulClientApi<undefined>;
  private publicClient: ContentfulClientApi<undefined>;
  private readonly authService = inject(AuthService);
  private readonly featureFlag = inject(FeatureFlagService);

  private getAccessToken = (): Promise<string> => {
    const token = this.authService.getTokenSnapshot();
    return Promise.resolve(token);
  };

  private createPublicClient(): ContentfulClientApi<undefined> {
    return createContentfulClient({
      space: AppConfig.connection.contentfulPublic.space,
      accessToken: AppConfig.connection.contentfulPublic.accessToken
    });
  }

  private createEmbargoedClient(): ContentfulClientApi<undefined> {
    if (this.featureFlag.isEnabled(FeatureFlag.EmbargoedAssets)) {
      return createContentfulClient({
        space: AppConfig.connection.contentful.space,
        // @ts-expect-error - contentful-sdk-core allows async functions for accessToken
        accessToken: this.getAccessToken,
        insecure: AppConfig.connection.contentful.insecure,
        host: AppConfig.connection.contentful.host,
        basePath: AppConfig.connection.contentful.basePath
      });
    } else {
      return createContentfulClient({
        space: AppConfig.connection.contentful.space,
        accessToken: AppConfig.connection.contentful.accessToken
      });
    }
  }

  private getEmbargoedClient(): ContentfulClientApi<undefined> {
    if (!this.embargoedClient) {
      this.embargoedClient = this.createEmbargoedClient();
    }
    return this.embargoedClient;
  }

  private getPublicClient(): ContentfulClientApi<undefined> {
    if (!this.publicClient) {
      this.publicClient = this.createPublicClient();
    }
    return this.publicClient;
  }

  public getPublicEntries(content_type?: ContentfulContentType): Observable<EntryCollection<EntrySkeletonType>> {
    return from(this.getPublicClient().getEntries<EntrySkeletonType>({ content_type })).pipe(
      catchError(() => of({} as EntryCollection<EntrySkeletonType>))
    );
  }

  public getEntries(content_type?: ContentfulContentType): Observable<EntryCollection<EntrySkeletonType>> {
    return from(this.getEmbargoedClient().getEntries<EntrySkeletonType>({ content_type })).pipe(
      catchError(() => of({} as EntryCollection<EntrySkeletonType>))
    );
  }
}
